/**
 * Mesh Part Ownership Engine
 *
 * Pure classification/audit layer. It never edits geometry, skin weights,
 * skeletons or bind matrices. Render adapters use its result to isolate
 * materials/transforms and to decide whether a part may receive independent
 * physics.
 */

export type MeshPartKind =
  | 'body'
  | 'garment'
  | 'hair'
  | 'armor'
  | 'rigid-carried'
  | 'accessory'
  | 'effect'
  | 'unknown'
  | 'monolithic-mixed'

export type MeshPartPhysicsOwnership =
  | 'body-surface'
  | 'skinned-garment'
  | 'secondary-garment'
  | 'rigid-socket'
  | 'visual-only'
  | 'safe-unknown'
  | 'monolithic-safe'

export interface MeshPartDescriptor {
  nodeName: string
  materialNames?: readonly string[]
  skinned: boolean
  boneCount?: number
  morphTargetCount?: number
  vertexCount?: number
  /** True when this is the only skinned presentation mesh in the actor. */
  soleSkinnedPresentation?: boolean
}

export interface MeshPartClassification {
  kind: MeshPartKind
  physics: MeshPartPhysicsOwnership
  confidence: number
  signals: string[]
  safeForIndependentTransform: boolean
  requiresDerivedSplit: boolean
}

export interface MeshPartAuditEntry extends MeshPartDescriptor {
  classification: MeshPartClassification
}

export interface MeshPartAudit {
  total: number
  byKind: Record<MeshPartKind, number>
  independentlyTransformable: number
  skinnedParts: number
  unknown: number
  monolithicMixed: number
  derivedSplitRequired: number
  entries: MeshPartAuditEntry[]
}

const BODY = [
  'body','skin','torso','chest','head','face','neck','arm','leg','hand','foot',
  'finger','toe','ear','nose','eye','mouth','teeth','tongue','lashes',
]
const GARMENT = [
  'cloth','clothes','garment','shirt','pants','trouser','robe','skirt','dress',
  'coat','jacket','cape','cloak','hood','sleeve','scarf','loin','tabard','tunic',
]
const HAIR = ['hair','beard','mustache','moustache','brow','ponytail','braid']
const ARMOR = [
  'armor','armour','plate','mail','pauldron','gauntlet','bracer','greave',
  'helmet','helm','cuirass','vambrace','shin','boot','shoulderpad',
]
const CARRIED = [
  'weapon','sword','blade','knife','dagger','axe','hammer','mace','spear','staff',
  'wand','gun','rifle','pistol','bow','crossbow','shield','lance','scythe','club',
]
const ACCESSORY = [
  'belt','buckle','bag','pouch','holster','quiver','necklace','amulet','ring',
  'bracelet','earring','goggle','glasses','mask','crown','horn','backpack',
]
const EFFECT = ['fx','vfx','effect','glow','aura','trail','energy','particle','hologram']

const normalize=(s:string)=>s
  .replace(/([a-z0-9])([A-Z])/g,'$1 $2')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g,' ')
const has=(hay:string,words:readonly string[])=>words.filter(w=>
  new RegExp(`(^| )${w}( |$)`).test(hay)||(w.length>=5&&hay.includes(w))
)
const score=(hits:string[],weight=1)=>Math.min(1,hits.length*.34*weight)

export function classifyMeshPart(input:MeshPartDescriptor):MeshPartClassification{
  const material=(input.materialNames??[]).join(' ')
  const hay=normalize(`${input.nodeName} ${material}`)
  const body=has(hay,BODY)
  const garment=has(hay,GARMENT)
  const hair=has(hay,HAIR)
  const armor=has(hay,ARMOR)
  const carried=has(hay,CARRIED)
  const accessory=has(hay,ACCESSORY)
  const effect=has(hay,EFFECT)

  const strongKinds=[
    body.length?'body':null,
    garment.length?'garment':null,
    hair.length?'hair':null,
    armor.length?'armor':null,
    carried.length?'rigid-carried':null,
    accessory.length?'accessory':null,
    effect.length?'effect':null,
  ].filter(Boolean) as MeshPartKind[]

  // A single SkinnedMesh/material carrying incompatible semantic groups cannot
  // be safely detached at runtime. Flag it for an offline derived split rather
  // than corrupting its bind pose.
  const inferredWholeBody = Boolean(input.skinned && input.soleSkinnedPresentation)
  const incompatible =
    (strongKinds.includes('body') || inferredWholeBody) &&
    (strongKinds.includes('garment')||strongKinds.includes('hair')||strongKinds.includes('armor')||strongKinds.includes('rigid-carried')||strongKinds.includes('accessory'))
  if(input.skinned&&incompatible){
    return{
      kind:'monolithic-mixed',
      physics:'monolithic-safe',
      confidence:.98,
      signals:[...new Set([
        ...(inferredWholeBody?['sole-skinned-presentation']:[]),
        ...body,...garment,...hair,...armor,...carried,...accessory,
      ])],
      safeForIndependentTransform:false,
      requiresDerivedSplit:true,
    }
  }

  let kind:MeshPartKind='unknown'
  let signals:string[]=[]
  let confidence=.15
  if(effect.length){kind='effect';signals=effect;confidence=score(effect,.9)}
  else if(carried.length){kind='rigid-carried';signals=carried;confidence=score(carried,1.15)}
  else if(armor.length){kind='armor';signals=armor;confidence=score(armor,1.05)}
  else if(garment.length){kind='garment';signals=garment;confidence=score(garment)}
  else if(hair.length){kind='hair';signals=hair;confidence=score(hair)}
  else if(accessory.length){kind='accessory';signals=accessory;confidence=score(accessory)}
  else if(body.length){kind='body';signals=body;confidence=score(body)}
  else if(inferredWholeBody){kind='body';signals=['sole-skinned-presentation'];confidence=.78}
  else if(input.skinned&&((input.boneCount??0)>0)){kind='body';signals=['skinned'];confidence=.42}

  const physics:MeshPartPhysicsOwnership=
    kind==='body'?'body-surface':
    kind==='garment'?(input.skinned?'skinned-garment':'secondary-garment'):
    kind==='hair'?(input.skinned?'skinned-garment':'secondary-garment'):
    kind==='armor'?(input.skinned?'skinned-garment':'rigid-socket'):
    kind==='rigid-carried'?(input.skinned?'monolithic-safe':'rigid-socket'):
    kind==='accessory'?(input.skinned?'skinned-garment':'rigid-socket'):
    kind==='effect'?'visual-only':'safe-unknown'

  const safeForIndependentTransform=
    !input.skinned&&(physics==='rigid-socket'||physics==='secondary-garment'||physics==='visual-only')

  return{
    kind,
    physics,
    confidence:Math.max(.05,Math.min(1,confidence)),
    signals,
    safeForIndependentTransform,
    requiresDerivedSplit:input.skinned&&kind==='rigid-carried',
  }
}

function emptyCounts():Record<MeshPartKind,number>{
  return{
    body:0,garment:0,hair:0,armor:0,'rigid-carried':0,accessory:0,effect:0,
    unknown:0,'monolithic-mixed':0,
  }
}

export function auditMeshPartOwnership(parts:readonly MeshPartDescriptor[]):MeshPartAudit{
  const entries=parts.map(part=>({...part,classification:classifyMeshPart(part)}))
  const byKind=emptyCounts()
  let independentlyTransformable=0,skinnedParts=0,derivedSplitRequired=0
  for(const entry of entries){
    byKind[entry.classification.kind]++
    if(entry.skinned)skinnedParts++
    if(entry.classification.safeForIndependentTransform)independentlyTransformable++
    if(entry.classification.requiresDerivedSplit)derivedSplitRequired++
  }
  return{
    total:entries.length,
    byKind,
    independentlyTransformable,
    skinnedParts,
    unknown:byKind.unknown,
    monolithicMixed:byKind['monolithic-mixed'],
    derivedSplitRequired,
    entries,
  }
}
